import { RGBColor } from 'react-color';

import tinycolor from 'tinycolor2';
import { LangCodeEnum } from 'shared/enums/LangCodeEnum';

export type CompanyPostEntry = {
  circleId: string;
  name: string;
  brandColor: string;
  timezone: string;
  settings: {
    isDashboardLanguagesEnabled?: boolean;
    shouldTranslateComments?: boolean;
    enabledLocales: string[];
    enabledDashboardLocales: string[];
    ssoEnabled: boolean;
    multiLanguageEnabled: boolean;
    customKioskCodesEnabled: boolean;
    aiSummariesEnabled?: boolean;
    hasAiSummariesEnabled?: boolean;
    headlinesEnabled?: boolean;
    hasHeadlinesEnabled?: boolean;
    actEnabled?: boolean;
    hasActEnabled?: boolean;
    hasRespondentExternalEventApiEnabled?: boolean;
    hasDemographyExternalEventApiEnabled?: boolean;
    hasDepartmentExternalEventApiEnabled?: boolean;
    hasAvailableSurveysLinkEnabled?: boolean;
    hasLifecycleSubscriptionExternalEventApiEnabled?: boolean;
    hasHrisApiEnabled?: boolean;
  };
  approxEmployeeNumber: number;
  sectors: number[];
  plan?: number; // TODO investigate if this can be removed and only present in clientApi
};

export type CompanyDto = CompanyPostEntry & {
  id: number;
  logo: string;
};

export type CompanyModel = {
  id?: number;
  name: string;
  circleId: string;
  brandColor?: RGBColor;
  timezone: string;
  settings: {
    isDashboardLanguagesEnabled?: boolean;
    shouldTranslateComments?: boolean;
    enabledLocales: LangCodeEnum[];
    enabledDashboardLocales: string[];
    ssoEnabled: boolean;
    multiLanguageEnabled: boolean;
    customKioskCodesEnabled: boolean;
    aiSummariesEnabled?: boolean;
    // TODO: AIS-189 remove all hasAiSummariesEnabled usages when project endpoint returns aiSummariesEnabled instead of hasAiSummariesEnabled
    hasAiSummariesEnabled?: boolean;
    headlinesEnabled?: boolean;
    hasHeadlinesEnabled?: boolean;
    actEnabled?: boolean;
    hasActEnabled?: boolean;
    hasRespondentExternalEventApiEnabled?: boolean;
    hasDemographyExternalEventApiEnabled?: boolean;
    hasDepartmentExternalEventApiEnabled?: boolean;
    hasAvailableSurveysLinkEnabled?: boolean;
    hasLifecycleSubscriptionExternalEventApiEnabled?: boolean;
    hasHrisApiEnabled?: boolean;
  };
  approxEmployeeNumber?: number;
  sectors?: number[];
  logo?: string;
  plan?: number;
};

export function mapCompanyModelToDto(model: CompanyModel): CompanyDto {
  return {
    id: model.id,
    name: model.name,
    logo: model.logo,
    brandColor: tinycolor(model.brandColor).toHexString(),
    circleId: model.circleId,
    timezone: model.timezone,
    settings: {
      isDashboardLanguagesEnabled: model.settings.isDashboardLanguagesEnabled,
      shouldTranslateComments: model.settings.shouldTranslateComments,
      enabledLocales: model.settings.enabledLocales,
      enabledDashboardLocales: model.settings.enabledDashboardLocales,
      ssoEnabled: model.settings.ssoEnabled,
      multiLanguageEnabled: model.settings.multiLanguageEnabled,
      customKioskCodesEnabled: model.settings.customKioskCodesEnabled,
      aiSummariesEnabled: Company.getAiSummariesEnabled(model),
      headlinesEnabled: Company.getHeadlinesEnabled(model),
      actEnabled: Company.getActEnabled(model),
      hasRespondentExternalEventApiEnabled: model.settings.hasRespondentExternalEventApiEnabled,
      hasDemographyExternalEventApiEnabled: model.settings.hasDemographyExternalEventApiEnabled,
      hasDepartmentExternalEventApiEnabled: model.settings.hasDepartmentExternalEventApiEnabled,
      hasAvailableSurveysLinkEnabled: model.settings.hasAvailableSurveysLinkEnabled,
      hasLifecycleSubscriptionExternalEventApiEnabled:
        model.settings.hasLifecycleSubscriptionExternalEventApiEnabled,
      hasHrisApiEnabled: model.settings.hasHrisApiEnabled,
    },
    sectors: model.sectors,
    approxEmployeeNumber: model.approxEmployeeNumber,
    plan: model.plan,
  };
}

export function mapCompanyDtoToModel(dto: CompanyDto): CompanyModel {
  return {
    id: dto.id,
    circleId: dto.circleId,
    name: dto.name,
    logo: dto.logo,
    sectors: dto.sectors,
    approxEmployeeNumber: dto.approxEmployeeNumber,
    brandColor: tinycolor(dto.brandColor).toRgb(),
    timezone: dto.timezone,
    settings: {
      isDashboardLanguagesEnabled: dto?.settings?.isDashboardLanguagesEnabled,
      shouldTranslateComments: dto?.settings?.shouldTranslateComments,
      enabledLocales: dto?.settings?.enabledLocales as LangCodeEnum[],
      enabledDashboardLocales: dto?.settings?.enabledDashboardLocales as string[],
      ssoEnabled: dto?.settings?.ssoEnabled,
      multiLanguageEnabled: dto?.settings?.multiLanguageEnabled,
      customKioskCodesEnabled: dto?.settings?.customKioskCodesEnabled,
      aiSummariesEnabled: dto?.settings?.aiSummariesEnabled,
      hasAiSummariesEnabled: dto?.settings?.hasAiSummariesEnabled,
      headlinesEnabled: dto?.settings?.headlinesEnabled,
      hasHeadlinesEnabled: dto?.settings?.hasHeadlinesEnabled,
      hasActEnabled: dto?.settings?.hasActEnabled,
      hasRespondentExternalEventApiEnabled: dto?.settings?.hasRespondentExternalEventApiEnabled,
      hasDemographyExternalEventApiEnabled: dto?.settings?.hasDemographyExternalEventApiEnabled,
      hasDepartmentExternalEventApiEnabled: dto?.settings?.hasDepartmentExternalEventApiEnabled,
      hasAvailableSurveysLinkEnabled: dto?.settings?.hasAvailableSurveysLinkEnabled,
      hasLifecycleSubscriptionExternalEventApiEnabled:
        dto?.settings?.hasLifecycleSubscriptionExternalEventApiEnabled,
      hasHrisApiEnabled: dto?.settings?.hasHrisApiEnabled,
    },
    plan: dto.plan,
  };
}

// TODO: AIS-189 remove all hasAiSummariesEnabled usages when project endpoint returns aiSummariesEnabled instead of hasAiSummariesEnabled
export namespace Company {
  export function getAiSummariesEnabled(company: CompanyModel): boolean {
    return company.settings.aiSummariesEnabled ?? company.settings.hasAiSummariesEnabled;
  }

  export function getHeadlinesEnabled(company: CompanyModel): boolean {
    return company.settings.headlinesEnabled ?? company.settings.hasHeadlinesEnabled;
  }

  export function getActEnabled(company: CompanyModel): boolean {
    return company.settings.actEnabled ?? company.settings.hasActEnabled;
  }
}
